import {
  CategoryName,
  DisplayModeName,
  IInParamsValues,
  TApplicationName,
  TDatabaseRecord,
  TDatasetName,
  tg,
} from '@infotech-soe/metaschema';
import { produce } from 'immer';
import { isEmpty } from 'lodash';

import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import { CategoryStateEntity } from '../../models/category/category';
import { DatasetGridViewModel } from '../../models/dataset/DatasetRelated';
import {
  DatasetConfig,
  LayoutConfig,
} from '../../models/user-config/DisplayModeConfig';
import { getLastReportCode } from '../commands/reportSelectionCache';
import UniqueId from '../uid/UniqueId';
import IUserConfigService from '../user-config/IUserConfigService';
import UserConfigService from '../user-config/UserConfigService';

import CategoryConditionsService from './CategoryConditionsService';
import ICategoryService from './ICategoryService';
import CategoryViewModel from './view-model/CategoryViewModel';
import CategoryViewModelSource from './view-model/CategoryViewModelSource';
import { DatasetLayoutViewModelType } from './view-model/DatasetLayoutViewModel';
import DisplayModeViewModelFactory from './vm-factory/DisplayModeViewModelFactory';

/**
 * Default implementation of {@link ICategoryService}.
 */
class CategoryServiceSingleton implements ICategoryService {
  private readonly userConfig: IUserConfigService;

  constructor() {
    this.userConfig = new UserConfigService();
  }

  public openDisplayMode(
    category: CategoryName,
    displayMode: DisplayModeName,
    inParams?: IInParamsValues,
    hideTree?: boolean,
    hideCheckbox?: boolean,
    layoutConfig?: LayoutConfig,
    withDefaultConfig?: boolean
  ): Promise<CategoryViewModel> {
    const factory = new DisplayModeViewModelFactory(
      category,
      displayMode,
      inParams,
      hideTree,
      hideCheckbox,
      withDefaultConfig
    );

    return factory.createViewModel(layoutConfig);
  }

  public async getItemFromPos(
    category: CategoryName,
    displayMode: DisplayModeName,
    inParams: IInParamsValues
  ): Promise<Record<TDatasetName, TDatabaseRecord>> {
    return new MerpsApiProvider().displayModes.getByPosParams(
      category,
      displayMode,
      inParams
    );
  }

  private async saveConditions({
    category: inputCategory,
    openMode,
  }: CategoryStateEntity): Promise<void> {
    const { category, displayMode, displayModeConditions } = produce(
      inputCategory.source,
      (source: CategoryViewModelSource): CategoryViewModelSource => {
        for (const datasetConditions of Object.values(
          source.displayModeConditions
        )) {
          datasetConditions.conditions =
            new CategoryConditionsService().sanitizeConditions(
              datasetConditions.conditions
            );
        }
        return source;
      }
    );

    await this.userConfig.saveConditionsConfig(
      category,
      `${displayMode}_${openMode}`,
      displayMode,
      displayModeConditions
    );
  }

  private async saveDatasetConfig(
    { category }: CategoryStateEntity,
    datasets: Record<UniqueId, DatasetGridViewModel>
  ): Promise<void> {
    const datasetsConfig: Record<TDatasetName, DatasetConfig> = {};
    const layoutConfig: LayoutConfig = {};
    const currentDisplayModeConfig = await this.userConfig.getDisplayModeConfig(
      category.source.category,
      category.source.displayMode,
      category.source.withDefaultConfig
    );

    const getPanelSize = (
      datasetId: UniqueId,
      horizontal: boolean
    ): number | undefined => {
      const isDataset = (elementId: UniqueId): boolean => {
        for (const element of Object.values(category.layout.elements)) {
          if (element.id === elementId) {
            if (element.type === DatasetLayoutViewModelType.SINGLE) {
              if (element.dataset === datasetId) {
                return true;
              }
            } else if (isDataset(element.children[0])) {
              return true;
            }
          }
        }
        return false;
      };

      for (const element of Object.values(category.layout.elements)) {
        if (
          horizontal &&
          element.type === DatasetLayoutViewModelType.HORIZONTALLY_STACKED
        ) {
          if (isDataset(element.children[0])) {
            return element.panelSize;
          }
        } else if (
          !horizontal &&
          element.type === DatasetLayoutViewModelType.VERTICALLY_STACKED
        ) {
          if (isDataset(element.children[0])) {
            return element.panelSize;
          }
        }
      }
    };

    for (const [datasetId, dataset] of Object.entries(category.datasets)) {
      const activeId = datasets[datasetId].catalogContext?.activeId;
      if (tg.isSomething(activeId)) {
        currentDisplayModeConfig.treeActiveId = activeId;
      }
      const ids = datasets[datasetId].catalogContext?.treeState?.ids;
      if (tg.isSomething(ids)) {
        const treeCheckedIds =
          ids && (ids.length > 1 || (ids.length === 1 && ids[0] !== activeId))
            ? ids
            : undefined;
        currentDisplayModeConfig.treeCheckedIds = treeCheckedIds;
      }

      const { showForm, ...config } = datasets[datasetId].config;
      datasetsConfig[dataset.name] = config;
      const panelHSize =
        getPanelSize(datasetId, true) ||
        currentDisplayModeConfig.layoutConfig?.datasets?.[dataset.name]
          ?.panelHSize;
      const panelVSize =
        getPanelSize(datasetId, false) ||
        currentDisplayModeConfig.layoutConfig?.datasets?.[dataset.name]
          ?.panelVSize;
      if (panelHSize || panelVSize) {
        if (!layoutConfig.datasets) {
          layoutConfig.datasets = { [dataset.name]: {} };
        } else {
          layoutConfig.datasets[dataset.name] = {};
        }
        if (panelHSize) {
          layoutConfig.datasets[dataset.name].panelHSize = panelHSize;
        }
        if (panelVSize) {
          layoutConfig.datasets[dataset.name].panelVSize = panelVSize;
        }
      }
    }

    currentDisplayModeConfig.datasetsConfig = datasetsConfig;
    const treePanelSize = category.layout.treePanelSize;
    if (treePanelSize) {
      layoutConfig.treePanelSize = treePanelSize;
    }
    if (!isEmpty(layoutConfig)) {
      currentDisplayModeConfig.layoutConfig = layoutConfig;
    }

    const lastReportCode = getLastReportCode(
      category.source.category,
      category.source.displayMode
    );
    if (lastReportCode) {
      currentDisplayModeConfig.lastReportCode = lastReportCode;
    }

    await this.userConfig.saveDisplayModeConfig(
      category.source.category,
      category.source.displayMode,
      currentDisplayModeConfig
    );
  }

  public async saveCategory(
    state: CategoryStateEntity,
    datasets: Record<UniqueId, DatasetGridViewModel>
  ): Promise<void> {
    await this.saveConditions(state);
    await this.saveDatasetConfig(state, datasets);
  }

  public async getDisplayMode(
    application: TApplicationName | null,
    category: CategoryName
  ): Promise<DisplayModeName> {
    const schemaRepository = new CachedSchemaRepository();
    return application
      ? schemaRepository
          .getApplication(application)
          .then(
            applicationSchema =>
              applicationSchema.Categories[category]?.defaultDisplay ?? 'main'
          )
      : 'main';
  }
}

const CategoryService = (() => {
  let instance: ICategoryService;
  return new Proxy(CategoryServiceSingleton, {
    construct(Target: typeof CategoryServiceSingleton): ICategoryService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default CategoryService;
